"use client";

import cn from "classnames";
import { ChangeEvent, FC, useEffect, useState } from "react";
import styles from "./SubnetCalculator.module.css";
import { getDataAutomationGroup } from "@/helpers";
import { MinusThick, PlusThick } from "@/icons";

interface NumberOfSubnetsInputProps {
  subnetBits: number;
  setSubnetBits: (value: number) => void;
  maskBits: number;
}

export const NumberOfSubnetsInput: FC<NumberOfSubnetsInputProps> = ({
  subnetBits,
  setSubnetBits,
  maskBits,
}) => {
  const [numberOfSubnets, setNumberOfSubnets] = useState<number>(subnetBits);

  useEffect(() => {
    const newSubnetBits = Math.pow(2, subnetBits);
    setNumberOfSubnets(newSubnetBits);
  }, [subnetBits]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const number = parseInt(event.target.value);
    setNumberOfSubnets(number);
  };

  const handleBlur = () => {
    const validatedNumberOfSubnets =
      isNaN(numberOfSubnets) || numberOfSubnets < 1 ? 1 : numberOfSubnets;

    const nearestExponentiation = Math.round(
      Math.log2(validatedNumberOfSubnets),
    );

    if (nearestExponentiation > 32 - maskBits) {
      setSubnetBits(32 - maskBits);
      const newNumberOfSubnets = Math.pow(2, 32 - maskBits);
      setNumberOfSubnets(newNumberOfSubnets);
    } else {
      const newNumberOfSubnets = Math.pow(2, nearestExponentiation);
      setSubnetBits(nearestExponentiation);
      setNumberOfSubnets(newNumberOfSubnets);
    }
  };

  const handleIncrement = () => {
    if (subnetBits < 32 - maskBits) {
      setSubnetBits(subnetBits + 1);
    }
  };

  const handleDecrement = () => {
    if (subnetBits > 0) {
      setSubnetBits(subnetBits - 1);
    }
  };

  const handleKeyDownDecrement = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleDecrement();
    }
  };

  const handleKeyDownIncrement = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleIncrement();
    }
  };

  return (
    <div className={cn("relative flex flex-col", styles.calc)}>
      <label
        className="text-body-xs text-swdc-gray-400 pb-2 font-bold"
        htmlFor="number-of-subnets"
      >
        Number of subnets
      </label>
      <input
        className="border-swdc-gray-200 text-swdc-gray-400 focus:outline-swdc-black-600 rounded-button h-10 w-full border-2 bg-white px-12 text-center md:w-48"
        id="number-of-subnets"
        type="number"
        inputMode="numeric"
        min={1}
        step={1}
        value={numberOfSubnets}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
        {...getDataAutomationGroup("input-field")}
      />
      <button
        className="text-swdc-black-600 hover:text-swdc-gray-300 focus-visible:text-swdc-gray-300 focus-visible:outline-swdc-blue-400 active:text-swdc-black-600 absolute bottom-0 left-5 h-10 px-1.5 focus-visible:outline-2"
        onClick={handleDecrement}
        onKeyDown={handleKeyDownDecrement}
        aria-label="Decrement number of subnets"
      >
        <MinusThick fill="currentColor" width={16} aria-hidden />
      </button>
      <button
        className="text-swdc-black-600 hover:text-swdc-gray-300 focus-visible:text-swdc-gray-300 focus-visible:outline-swdc-blue-400 active:text-swdc-black-600 absolute right-5 bottom-0 h-10 px-1.5 focus-visible:outline-2"
        onClick={handleIncrement}
        onKeyDown={handleKeyDownIncrement}
        aria-label="Increment number of subnets"
      >
        <PlusThick fill="currentColor" width={16} aria-hidden />
      </button>
    </div>
  );
};
